import * as React from "react";
import "normalize.css";
import { Helmet } from "react-helmet";
import { createGlobalStyle } from "styled-components";

import Header from "./header.js";
import Footer from "./footer.js";
import { graphql, useStaticQuery } from "gatsby";
import GlobalStyle from "../styles/globalStyle.js";
import PageHeader from "./pageHeader.js";
import styled, { ThemeProvider } from "styled-components";
import theme, { when } from "../styles/theme.js";
import { useInView } from "react-intersection-observer";
import { StaticImage } from "gatsby-plugin-image";
import backToTopImage from "../images/back_to_top.svg";
import { useEffect, useState } from "react";
import { useLocalization } from "gatsby-theme-i18n";

const EnGlobalStyle = createGlobalStyle`
  h1, h2, h3, h4, h5, h6 {
    font-weight: 600;
    line-height: 1.2;
  }
`;

const Main = styled.main`
  flex: 1;
`;

const BackgroundDecos = styled.div`
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: -1;

  ${when("lg")} {
    display: block;
  }
`;

const BackgroundDeco = styled.div`
  width: 300px;
  height: 300px;
  position: absolute;
  top: 100vh;
  left: 0;
  overflow: hidden;
`;

const BackgroundDecoInner = styled.div`
  transform: translateX(-10rem) rotate(-15deg);
`;

const BackgroundDecoAlt = styled(BackgroundDeco)`
  top: 200vh;
  left: unset;
  right: 0;
`;

const BackgroundDecoInnerAlt = styled.div`
  transform: translateX(10rem) rotate(30deg);
`;

const BackToTopButton = styled.div`
  position: fixed;
  bottom: 5rem;
  right: 2rem;
  opacity: ${(props) => (props.isActive ? 1 : 0)};
  transition: opacity 0.25s ease-in-out;
  pointer-events: ${(props) => (props.isActive ? "auto" : "none")};
  z-index: 100;
`;

const BackToTopButtonInner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
  width: 5rem;
  height: 5rem;
  border-radius: 50%;
  cursor: pointer;
`;

const BackToTopImage = styled.img`
  display: block;
  width: 3rem;
  height: 3rem;
`;

const Layout = ({
  children,
  title,
  description,
  uri,
  image,
  imageAlt,
  mainNav,
  globalNav,
  footerNav,
  originalPath,
  headerTitle,
  headerBackgroundImage,
  settings,
  atTop,
  hasDeco = true,
  brands,
}) => {
  const {
    site: {
      siteMetadata: { fallbackTitle, fallbackDescription, siteUrl, themeColor },
    },
    // allContentfulPageSectionBrands: { nodes },
  } = useStaticQuery(graphql`
    query HeaderQuery {
      site {
        siteMetadata {
          fallbackTitle
          fallbackDescription
          siteUrl
          themeColor
        }
      }
    }
  `);
  const { locale } = useLocalization();
  const finalTitle = title || fallbackTitle;
  const finalDescription = description || fallbackDescription;
  const { ref, inView } = useInView({
    initialInView: true,
    rootMargin: "-30% 0% -30% 0%",
  });

  const [hasScrollTop, setHasScrollTop] = useState(false);

  const onScroll = () => {
    if (
      document.body.scrollTop > 320 ||
      document.documentElement.scrollTop > 320
    ) {
      setHasScrollTop(true);
    } else {
      setHasScrollTop(false);
    }
  };

  useEffect(() => {
    if (!document) {
      return;
    }

    onScroll();
    document.addEventListener("scroll", onScroll);

    return () => {
      document.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin />
        <link
          href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&family=Noto+Sans+TC:wght@400;500;600;700&family=Noto+Sans+SC:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <title>{finalTitle}</title>
        <meta name="description" content={finalDescription} />
        <meta property="og:title" content={finalTitle} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${siteUrl}${uri}`} />
        <meta name="theme-color" content={themeColor} />
        {image && <meta property="og:image" content={`${siteUrl}${image}`} />}
        {image && <meta property="og:image:width" content="1200" />}
        {image && <meta property="og:image:height" content="630" />}
        {image && <meta property="og:image:alt" content={imageAlt} />}
      </Helmet>
      <GlobalStyle />
      {locale === "en" && <EnGlobalStyle />}
      <ThemeProvider theme={theme}>
        {hasDeco && (
          <BackgroundDecos>
            <BackgroundDeco>
              <BackgroundDecoInner>
                <StaticImage
                  src="../images/background_pattern_dark.png"
                  alt=""
                  placeholder="none"
                  width={300}
                  height={300}
                />
              </BackgroundDecoInner>
            </BackgroundDeco>
            <BackgroundDecoAlt>
              <BackgroundDecoInnerAlt>
                <StaticImage
                  src="../images/background_pattern_dark.png"
                  alt=""
                  placeholder="none"
                  width={300}
                  height={300}
                />
              </BackgroundDecoInnerAlt>
            </BackgroundDecoAlt>
          </BackgroundDecos>
        )}

        <Header
          mainNav={mainNav}
          globalNav={globalNav}
          originalPath={originalPath}
          settings={settings}
          atTop={atTop !== undefined ? atTop : inView}
          brands={brands}
        />
        <Main>
          {headerBackgroundImage && (
            <PageHeader
              ref={ref}
              title={headerTitle}
              backgroundImage={headerBackgroundImage}
            />
          )}
          {children}
        </Main>
        <BackToTopButton isActive={hasScrollTop}>
          <BackToTopButtonInner
            onClick={() => {
              document.body.scrollTop = 0; // For Safari
              document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Ope
            }}
          >
            <BackToTopImage
              src={backToTopImage}
              alt={locale === "en" ? "Back to top" : "顶部"}
            />
          </BackToTopButtonInner>
        </BackToTopButton>
        <Footer footerNav={footerNav} settings={settings} />
      </ThemeProvider>
    </>
  );
};

export default Layout;
